"use client";

import { useMemo } from "react";
import { isMobile } from "react-device-detect";
import { twMerge } from "tailwind-merge";

import { ErrorView } from "@/components/error-view";
import { formatDollarNumber } from "@/features/analysis/utils/formatters";
import { Lang } from "@/i18n-config";
import { getTranslations } from "@/utils/i18n/get-translations";

import { Skeletons } from "ui/widget/skeletons";

import { InsiderStatsOpenState, Top5Index } from "../data/types";
import { useInsiderBuyingTop5 } from "../services/insider-buying-top5";
import { ViewAllButton } from "./ViewAllButton";

type Props = {
  lang: Lang;
  symbol: string;
  openState: InsiderStatsOpenState;
  top5: Top5Index;
};

export const Top5Stats = ({ lang, symbol, openState, top5 }: Props) => {
  const { t } = getTranslations(lang);
  const {
    data: buyingTop5Data,
    isLoading,
    isError,
  } = useInsiderBuyingTop5({ symbol });

  const selectedTop5 = buyingTop5Data?.dropDown?.[top5]?.member ?? "";
  const selectedTop5Data = useMemo(
    () => buyingTop5Data?.point?.[selectedTop5] ?? [],
    [buyingTop5Data, selectedTop5]
  );
  const showViewAllButton = useMemo(
    () => isMobile && selectedTop5Data.length > 5,
    [selectedTop5Data.length]
  );
  const displayData = useMemo(
    () =>
      showViewAllButton && openState === "less"
        ? selectedTop5Data.slice(0, 5)
        : selectedTop5Data,
    [openState, selectedTop5Data, showViewAllButton]
  );

  return isLoading ? (
    <Skeletons rows={6} className="space-y-5 " />
  ) : isError ? (
    <ErrorView className="h-80">{t.error.serverError}</ErrorView>
  ) : selectedTop5Data.length === 0 ? null : (
    <div>
      <ul className="divide-y-[1px] divide-zinc-300 overflow-auto px-5 sm:max-h-[380px]">
        {displayData.map((item, index) => (
          <li key={index} className="flex items-center justify-between py-4">
            <TradeBadge text={item?.actionSeries ?? "-"} />
            <div className="flex flex-col gap-y-2 text-right">
              <p className="text-sm text-zinc-600">
                <span
                  className={twMerge(
                    "font-semibold",
                    item.tradeNumber
                      ? item.tradeNumber > 0
                        ? "text-green-600"
                        : "text-red-600"
                      : ""
                  )}
                >
                  {item.tradeNumber?.toLocaleString() ?? "-"}
                </span>
                <span className="font-semibold">
                  {` ${t.analysis.chip.insider.shares}`}
                </span>
                <span className="font-medium">
                  {` / ${t.analysis.chip.insider.total} `}
                  {formatDollarNumber(item.tradeCash).replace("-", "")}
                </span>
              </p>
              <p className="text-xs text-zinc-500">
                <span>{item.tradeAt?.replace("/", "-") ?? "-"}</span>
                <span>{`・${t.analysis.chip.insider.close} `}</span>
                <span>{`$${item.price?.toFixed(1) ?? "-"}`}</span>
              </p>
            </div>
          </li>
        ))}
      </ul>
      {showViewAllButton && <ViewAllButton openState={openState} />}
    </div>
  );
};

const TradeBadge = ({ text }: { text: string }) => (
  <span
    className={twMerge(
      "rounded-full px-2 py-[2px] text-base font-medium",
      text.startsWith("B")
        ? "bg-green-100 text-green-700"
        : "bg-red-100 text-red-700"
    )}
  >
    {text}
  </span>
);
